import { useEffect, useState } from "react";
import type { ClinicResult } from "../setup-flow-config";

interface Props {
    clinics: ClinicResult[];
    onSelect: (clinic: ClinicResult) => void;
    onNoneMatch: () => void;
}

export default function ClinicPickStep({ clinics, onSelect, onNoneMatch }: Props) {
    const [focusedIndex, setFocusedIndex] = useState(0);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setFocusedIndex((i) => (i + 1) % clinics.length);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setFocusedIndex((i) => (i - 1 + clinics.length) % clinics.length);
                    break;
                case "Enter":
                    e.preventDefault();
                    onSelect(clinics[focusedIndex]);
                    break;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [focusedIndex, clinics, onSelect]);

    return (
        <div className="flex w-full max-w-[520px] flex-col items-center gap-10">
            <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">
                Which clinic is yours?
            </h1>

            {/* Selectable clinic cards */}
            <div className="flex w-full flex-col gap-3">
                {clinics.map((clinic, i) => (
                    <button
                        key={i}
                        onClick={() => onSelect(clinic)}
                        onMouseEnter={() => setFocusedIndex(i)}
                        className={`rounded-xl border px-6 py-4 text-left transition-colors ${
                            focusedIndex === i
                                ? "ring-2 ring-inset ring-max-900 bg-max-50"
                                : "border-shadow-100 bg-white hover:border-shadow-200"
                        }`}
                    >
                        <p className="text-lg font-semibold text-shadow-700">{clinic.name}</p>
                        <p className="mt-0.5 text-base text-shadow-400">{clinic.formattedAddress}</p>
                    </button>
                ))}
            </div>

            <p className="text-sm text-shadow-400">
                Use{" "}
                <kbd className="rounded border border-shadow-100 bg-shadow-25 px-1.5 py-0.5 font-mono text-xs">↑↓</kbd>{" "}
                then{" "}
                <kbd className="rounded border border-shadow-100 bg-shadow-25 px-1.5 py-0.5 font-mono text-xs">
                    Enter ↵
                </kbd>
            </p>

            {/* None of these link */}
            <button onClick={onNoneMatch} className="text-base text-shadow-500 hover:text-shadow-500 focused">
                None of these — I'll enter it manually
            </button>
        </div>
    );
}
